import React, { useState, useCallback } from 'react';
import { Loader2, Plus, Trash2, Check, Phone, Mail, Users, CalendarDays, StickyNote, Flame } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const KINDS = {
    call: { label: 'Appel', icon: Phone },
    email: { label: 'Email', icon: Mail },
    meeting: { label: 'Réunion', icon: Users },
    event: { label: 'Événement', icon: CalendarDays },
    note: { label: 'Note', icon: StickyNote },
};

export default function NetworkCrmTab({ contact, interactions, tasks, engagement, assignees, api, reload }) {
    const [kind, setKind] = useState('call');
    const [summary, setSummary] = useState('');
    const [savingInt, setSavingInt] = useState(false);
    const [taskTitle, setTaskTitle] = useState('');
    const [taskDue, setTaskDue] = useState('');

    const addInteraction = useCallback(async () => {
        if (!summary.trim()) { toast.error('Résumé requis'); return; }
        setSavingInt(true);
        try {
            await api.post(`/network/contacts/${contact.id}/interactions`, { kind, summary: summary.trim() });
            setSummary('');
            toast.success('Interaction enregistrée');
            reload();
        } catch { toast.error('Échec'); }
        finally { setSavingInt(false); }
    }, [api, contact.id, kind, summary, reload]);

    const deleteInteraction = async (id) => {
        try { await api.delete(`/network/interactions/${id}`); reload(); } catch { toast.error('Échec'); }
    };

    const addTask = async () => {
        if (!taskTitle.trim()) return;
        try {
            await api.post('/network/tasks', { title: taskTitle.trim(), due_at: taskDue || null, contact_id: contact.id });
            setTaskTitle(''); setTaskDue('');
            toast.success('Rappel créé');
            reload();
        } catch { toast.error('Échec'); }
    };

    const toggleTask = async (t) => {
        try { await api.put(`/network/tasks/${t.id}`, { done: !t.done }); reload(); } catch { toast.error('Échec'); }
    };

    const assign = async (userId) => {
        try {
            await api.put(`/network/contacts/${contact.id}`, { assigned_to: userId || null });
            toast.success(userId ? 'Contact attribué' : 'Attribution retirée');
            reload();
        } catch { toast.error('Échec'); }
    };

    return (
        <div className="space-y-6" data-testid="network-crm-tab">
            {/* Engagement + attribution */}
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-3 text-center" data-testid="crm-engagement">
                    <div className="flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wider text-foreground/50 font-semibold">
                        <Flame size={11} className="text-primary" /> Engagement
                    </div>
                    <div className="font-heading text-3xl mt-1">{engagement?.score ?? 0}<span className="text-sm text-foreground/40">/100</span></div>
                    <div className="text-[11px] text-foreground/50">
                        {engagement?.count ?? 0} interaction{(engagement?.count ?? 0) > 1 ? 's' : ''}
                        {engagement?.last_at && ` · dernière le ${new Date(engagement.last_at).toLocaleDateString('fr-FR')}`}
                    </div>
                </div>
                <div className="rounded-lg border border-border p-3">
                    <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-1.5">Attribué à</div>
                    <select
                        className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={contact.assigned_to || ''}
                        onChange={e => assign(e.target.value)}
                        data-testid="crm-assign-select"
                    >
                        <option value="">— Non attribué —</option>
                        {(assignees || []).map(a => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
                    </select>
                </div>
            </div>

            {/* Nouvelle interaction */}
            <div className="rounded-lg border border-border p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold">Journal des interactions</div>
                <div className="flex gap-2">
                    <select className="h-9 rounded-md border border-input bg-background px-2 text-sm w-32"
                            value={kind} onChange={e => setKind(e.target.value)} data-testid="crm-interaction-kind">
                        {Object.entries(KINDS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                    </select>
                    <Textarea rows={1} className="flex-1 min-h-9" placeholder="Résumé de l'interaction…"
                              value={summary} onChange={e => setSummary(e.target.value)} data-testid="crm-interaction-summary" />
                    <Button size="sm" onClick={addInteraction} disabled={savingInt} data-testid="crm-interaction-add">
                        {savingInt ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                    </Button>
                </div>
                {(interactions || []).length === 0 ? (
                    <div className="text-xs text-foreground/50 py-2">Aucune interaction enregistrée.</div>
                ) : (
                    <div className="space-y-1.5 max-h-56 overflow-y-auto">
                        {interactions.map(i => {
                            const m = KINDS[i.kind] || KINDS.note;
                            return (
                                <div key={i.id} className="flex items-start gap-2.5 rounded-lg border border-border/60 px-3 py-2" data-testid="crm-interaction-row">
                                    <div className="h-7 w-7 rounded-md bg-primary-light text-primary flex items-center justify-center shrink-0 mt-0.5">
                                        <m.icon size={13} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm">{i.summary}</div>
                                        <div className="text-[11px] text-foreground/50">{m.label} · {new Date(i.happened_at).toLocaleString('fr-FR')}</div>
                                    </div>
                                    <button type="button" onClick={() => deleteInteraction(i.id)}
                                            className="text-foreground/30 hover:text-red-500 transition-colors" data-testid="crm-interaction-delete">
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Rappels liés */}
            <div className="rounded-lg border border-border p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold">Rappels & tâches</div>
                <div className="flex gap-2">
                    <Input className="flex-1" placeholder="Nouveau rappel pour ce contact…"
                           value={taskTitle} onChange={e => setTaskTitle(e.target.value)}
                           onKeyDown={e => e.key === 'Enter' && addTask()} data-testid="crm-contact-task-title" />
                    <Input type="datetime-local" className="w-48" value={taskDue} onChange={e => setTaskDue(e.target.value)} />
                    <Button size="sm" onClick={addTask} data-testid="crm-contact-task-add"><Plus size={13} /></Button>
                </div>
                {(tasks || []).length === 0 ? (
                    <div className="text-xs text-foreground/50 py-2">Aucun rappel pour ce contact.</div>
                ) : (
                    <div className="space-y-1.5">
                        {tasks.map(t => (
                            <div key={t.id} className={`flex items-center gap-2.5 rounded-lg border border-border/60 px-3 py-2 ${t.done ? 'opacity-50' : ''}`} data-testid="crm-contact-task-row">
                                <button type="button" onClick={() => toggleTask(t)}
                                        className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 ${t.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-border hover:border-primary'}`}
                                        data-testid="crm-contact-task-toggle">
                                    {t.done && <Check size={11} />}
                                </button>
                                <div className="flex-1 min-w-0">
                                    <div className={`text-sm truncate ${t.done ? 'line-through' : ''}`}>{t.title}</div>
                                    {t.due_at && <div className="text-[11px] text-foreground/50">{new Date(t.due_at).toLocaleString('fr-FR')}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
